import type React from 'react'
import type {
  CMSButtonLink,
  CMSComponentResponsiveImage,
  CMSComponentVideo,
  CMSLinkUrl,
  CMSSectionTheme,
} from '@/types/sanity'
import { ResponsiveImage } from '@/components/Shared/ResponsiveImage'
import { Video } from '@/components/Shared/Video'
import { LinkButton } from '@/components/Link/LinkButton'
import {
  PortableText,
  type PortableTextReactComponents,
} from '@portabletext/react'

import styles from './LegacyVariant.module.scss'
import { RichTextBlock } from '../SectionRichText/RichTextBlock'
import { Wrapper } from './FullBleedHeroBannerWrapper.tsx'

export type LegacyConfig = {
  bannerType: 'legacy' | 'fullBleed' | 'inset'
  eyebrow?: string
  eyebrowColor?: string
  header?: string
  description?: any
  cta?: CMSButtonLink
  theme?: CMSSectionTheme
  contentAlignment: 'left' | 'center'
  moduleUrl?: CMSLinkUrl
  isModuleClickable?: boolean
  content: {
    image?: CMSComponentResponsiveImage
    video?: CMSComponentVideo
  }
}

const portableTextComponents: Partial<PortableTextReactComponents> = {
  block: RichTextBlock,
  types: {},
}

export const LegacyVariant: React.FC<{ config: LegacyConfig }> = ({
  config,
}) => {
  const {
    eyebrow,
    eyebrowColor,
    header,
    description,
    cta,
    content,
    contentAlignment,
    isModuleClickable,
    moduleUrl,
  } = config

  return (
    <Wrapper
      isModuleClickable={isModuleClickable}
      moduleUrl={moduleUrl}
      content={content}
    >
      <div
        className={`${styles.container} px-32 py-32`}
        style={{ textAlign: contentAlignment }}
      >
        {eyebrow && (
          <p
            className="type-label mb-16"
            style={{ color: eyebrowColor || undefined }}
          >
            {eyebrow}
          </p>
        )}
        {header && (
          <h1
            className="type-h1 mb-16"
            dangerouslySetInnerHTML={{ __html: header! }}
          />
        )}
        {content?.video ? (
          <Video
            videoUrl={content.video.video.secureUrl}
            videoType={content.video.videoType}
            ratios={content.video.ratios}
            dialogueThumbnail={content.video.dialogue?.thumbnail}
            includeControls={content.video.dialogue?.includeControls}
            loopVideo={content.video.background?.loopVideo}
            captionsUrl={content.video.dialogue?.captionsUrl}
            backupImage={content.video.backupImage}
            classes={styles.video}
          />
        ) : content?.image ? (
          <ResponsiveImage document={content.image} />
        ) : null}
        {description && (
          <div
            className="mb-32 mt-32"
            style={{ '--text-align': contentAlignment } as any}
          >
            <PortableText
              value={description}
              components={portableTextComponents}
            />
          </div>
        )}
        {cta && cta.text && <LinkButton link={cta} />}
      </div>
    </Wrapper>
  )
}
